/**
 * Demo Treasury fixtures — a fully-faked, deterministic "Acme Capital" org.
 *
 * Every object below is typed against the REAL production types in
 * `src/types/*`. TypeScript strict mode is the sync forcing-function: if a
 * required field is added to `Vault`/`TxProposal`/etc., this module fails to
 * compile until the fixture is updated (DEMO_TREASURY_DESIGN.md §4.1 / §9).
 *
 * SAFETY (§8): all identities use the obviously-non-derivable `demo:` prefix
 * (fails real WK P2WSH validation), the orgIndex uses a reserved sentinel, and
 * every address/amount is clearly fictional. Nothing here is, or can become, a
 * real signing input — this module imports NOTHING from the signing/wallet
 * layer.
 */

import type { User } from '@/types/auth';
import type {
  Organization,
  OrganizationWithMembership,
  OrganizationDetail,
  Member,
  Membership,
  OrgAuditLog,
  CriticalActionLogEntry,
} from '@/types/organization';
import type {
  Vault,
  VaultDetail,
  VaultMember,
  VaultAddress,
  VaultBalance,
  VaultTransaction,
  VaultBalanceHistory,
  TxProposal,
  VaultPolicy,
} from '@/types/vault';

// ============================================================
// Sentinels (deliberately non-derivable / fictional)
// ============================================================

/** Reserved demo orgIndex sentinel — outside the real 100–99999 allocation band. */
export const DEMO_ORG_INDEX = 0;

/** Demo org id — referenced everywhere a real orgId would be. */
export const DEMO_ORG_ID = 'demo-org-acme';

/** wkIdentity sentinels — `demo:` prefix fails real P2WSH validation server-side. */
export const DEMO_WK = {
  owner: 'demo:acme-owner',
  admin: 'demo:acme-admin',
  member: 'demo:acme-member',
} as const;

/** Vault ids used by the demo routes + walkthrough hero. */
export const DEMO_VAULT_IDS = {
  btc: 'demo-vault-btc',
  eth: 'demo-vault-eth',
  cold: 'demo-vault-cold',
} as const;

/** The hero EVM payroll proposal the signature walkthrough plays against. */
export const DEMO_HERO_PROPOSAL_ID = 'demo-proposal-payroll';

const NOW = Date.UTC(2026, 5, 23, 12, 0, 0); // 2026-06-23T12:00:00Z — deterministic
const iso = (msFromNow: number): string => new Date(NOW + msFromNow).toISOString();
const DAY = 86_400_000;
const HOUR = 3_600_000;

/**
 * Safe array index for fixtures — avoids the `!` non-null assertion that the
 * strict eslint config forbids while keeping the call sites terse. Throws if the
 * index is out of range (only possible from a fixture authoring bug).
 */
function at<T>(arr: readonly T[], i: number): T {
  const v = arr[i];
  if (v === undefined) {
    throw new Error(`demoFixtures: missing array element at index ${String(i)}`);
  }
  return v;
}

// ============================================================
// Inlined crypto rates (no live rates.ts call in demo)
// ============================================================

// Explicit numeric constants so fixtures never need a non-null assertion on a
// Record lookup (the eslint strict config forbids `!`).
const RATE_BTC = 64250;
const RATE_ETH = 3180;
const RATE_FLUX = 0.62;

export const demoCryptoRates: Record<string, number> = {
  btc: RATE_BTC,
  eth: RATE_ETH,
  flux: RATE_FLUX,
  // USDT/USDC priced ~1:1; tokens render via token decimals, native rate unused.
};

// ============================================================
// The demo user (read-only viewer of Acme Capital, "Owner" role)
// ============================================================

export const demoUser: User = {
  wkIdentity: DEMO_WK.owner,
  enterpriseEmail: 'treasury@acme.example',
  displayName: 'Demo User',
  accountStatus: 'complete',
};

// ============================================================
// Organization + members
// ============================================================

const demoOrganization: Organization = {
  id: DEMO_ORG_ID,
  name: 'Acme Capital',
  slug: 'acme-capital',
  orgIndex: DEMO_ORG_INDEX,
  description: 'Multi-asset treasury for Acme Capital (demo).',
  createdBy: DEMO_WK.owner,
  createdAt: iso(-180 * DAY),
  updatedAt: iso(-2 * DAY),
  settings: {
    allowMemberInvites: true,
    spamFilterEnabled: true,
  },
};

const ownerMembership: Membership = {
  id: 'demo-mem-owner',
  organizationId: DEMO_ORG_ID,
  wkIdentity: DEMO_WK.owner,
  email: 'treasury@acme.example',
  displayName: 'Dana Okafor',
  role: 'owner',
  joinedAt: iso(-180 * DAY),
  accountStatus: 'complete',
};

const adminMembership: Membership = {
  id: 'demo-mem-admin',
  organizationId: DEMO_ORG_ID,
  wkIdentity: DEMO_WK.admin,
  email: 'cfo@acme.example',
  displayName: 'Marco Reyes',
  role: 'admin',
  joinedAt: iso(-160 * DAY),
  invitedBy: DEMO_WK.owner,
  accountStatus: 'complete',
};

const memberMembership: Membership = {
  id: 'demo-mem-member',
  organizationId: DEMO_ORG_ID,
  wkIdentity: DEMO_WK.member,
  email: 'ops@acme.example',
  displayName: 'Priya Nair',
  role: 'member',
  joinedAt: iso(-120 * DAY),
  invitedBy: DEMO_WK.owner,
  accountStatus: 'complete',
};

export const demoMembers: Member[] = [ownerMembership, adminMembership, memberMembership];

export const demoOrgWithMembership: OrganizationWithMembership = {
  ...demoOrganization,
  membership: ownerMembership,
};

export const demoOrgDetail: OrganizationDetail = {
  ...demoOrganization,
  membership: ownerMembership,
  memberCount: demoMembers.length,
};

// ============================================================
// Vaults (3 × differing M-of-N)
// ============================================================

const btcVault: Vault = {
  id: DEMO_VAULT_IDS.btc,
  organizationId: DEMO_ORG_ID,
  name: 'BTC Treasury',
  description: '2-of-3 Bitcoin reserve.',
  chain: 'btc',
  vaultIndex: 0,
  requiredSigners: 2,
  totalSigners: 3,
  status: 'active',
  addressCount: 3,
  createdBy: DEMO_WK.owner,
  createdAt: iso(-170 * DAY),
  updatedAt: iso(-1 * DAY),
  signingMode: 'dual',
  tags: ['reserve'],
  totalBalanceUsd: 0, // filled below from balances
  memberCount: 3,
  lastActivityAt: iso(-1 * DAY),
};

const ethVault: Vault = {
  id: DEMO_VAULT_IDS.eth,
  organizationId: DEMO_ORG_ID,
  name: 'ETH Ops Wallet',
  description: '2-of-2 Ethereum operations wallet for payroll & vendors.',
  chain: 'eth',
  vaultIndex: 1,
  requiredSigners: 2,
  totalSigners: 2,
  status: 'active',
  addressCount: 1,
  createdBy: DEMO_WK.owner,
  createdAt: iso(-150 * DAY),
  updatedAt: iso(-2 * HOUR),
  signingMode: 'dual',
  tags: ['operations'],
  totalBalanceUsd: 0,
  memberCount: 2,
  lastActivityAt: iso(-2 * HOUR),
};

const coldVault: Vault = {
  id: DEMO_VAULT_IDS.cold,
  organizationId: DEMO_ORG_ID,
  name: 'Cold Reserve',
  description: '3-of-5 long-term cold storage.',
  chain: 'flux',
  vaultIndex: 2,
  requiredSigners: 3,
  totalSigners: 5,
  status: 'active',
  addressCount: 2,
  createdBy: DEMO_WK.owner,
  createdAt: iso(-160 * DAY),
  updatedAt: iso(-10 * DAY),
  signingMode: 'dual',
  tags: ['cold', 'reserve'],
  totalBalanceUsd: 0,
  memberCount: 5,
  lastActivityAt: iso(-10 * DAY),
};

// ============================================================
// Addresses
// ============================================================

const btcAddresses: VaultAddress[] = [
  {
    id: 'demo-addr-btc-0',
    vaultId: DEMO_VAULT_IDS.btc,
    chain: 'btc',
    addressIndex: 0,
    address: 'bc1qdemo0acmetreasuryxxxxxxxxxxxxxxxxxxv9q8a7',
    witnessScript: 'DEMO_WITNESS_SCRIPT_0',
    label: 'Primary',
    createdAt: iso(-170 * DAY),
  },
  {
    id: 'demo-addr-btc-1',
    vaultId: DEMO_VAULT_IDS.btc,
    chain: 'btc',
    addressIndex: 1,
    address: 'bc1qdemo1acmetreasuryxxxxxxxxxxxxxxxxxxk3m2p5',
    witnessScript: 'DEMO_WITNESS_SCRIPT_1',
    label: 'Deposits',
    createdAt: iso(-120 * DAY),
  },
  {
    id: 'demo-addr-btc-2',
    vaultId: DEMO_VAULT_IDS.btc,
    chain: 'btc',
    addressIndex: 2,
    address: 'bc1qdemo2acmetreasuryxxxxxxxxxxxxxxxxxxh8t6w1',
    witnessScript: 'DEMO_WITNESS_SCRIPT_2',
    label: 'Change',
    createdAt: iso(-60 * DAY),
  },
];

const ethAddresses: VaultAddress[] = [
  {
    id: 'demo-addr-eth-0',
    vaultId: DEMO_VAULT_IDS.eth,
    chain: 'eth',
    addressIndex: 0,
    address: '0xDeMo0000AcmeOps0000000000000000000001A7c9',
    label: 'Ops Smart Account',
    createdAt: iso(-150 * DAY),
  },
];

const coldAddresses: VaultAddress[] = [
  {
    id: 'demo-addr-cold-0',
    vaultId: DEMO_VAULT_IDS.cold,
    chain: 'flux',
    addressIndex: 0,
    address: 't1demo0AcmeColdReservexxxxxxxxxxxxxxxR4n8',
    redeemScript: 'DEMO_REDEEM_SCRIPT_0',
    label: 'Cold 1',
    createdAt: iso(-160 * DAY),
  },
  {
    id: 'demo-addr-cold-1',
    vaultId: DEMO_VAULT_IDS.cold,
    chain: 'flux',
    addressIndex: 1,
    address: 't1demo1AcmeColdReservexxxxxxxxxxxxxxxQ7p2',
    redeemScript: 'DEMO_REDEEM_SCRIPT_1',
    label: 'Cold 2',
    createdAt: iso(-140 * DAY),
  },
];

const addressesByVault: Record<string, VaultAddress[]> = {
  [DEMO_VAULT_IDS.btc]: btcAddresses,
  [DEMO_VAULT_IDS.eth]: ethAddresses,
  [DEMO_VAULT_IDS.cold]: coldAddresses,
};

// ============================================================
// Balances (multi-asset)
// ============================================================

const btcBalances: VaultBalance[] = [
  {
    id: 'demo-bal-btc-0',
    vaultId: DEMO_VAULT_IDS.btc,
    addressIndex: 0,
    chain: 'btc',
    address: at(btcAddresses, 0).address,
    balance: '184000000', // 1.84 BTC
    unconfirmedBalance: '0',
    balanceUsd: 1.84 * RATE_BTC,
    lastSyncedAt: iso(-1 * HOUR),
  },
  {
    id: 'demo-bal-btc-1',
    vaultId: DEMO_VAULT_IDS.btc,
    addressIndex: 1,
    chain: 'btc',
    address: at(btcAddresses, 1).address,
    balance: '52000000', // 0.52 BTC
    unconfirmedBalance: '0',
    balanceUsd: 0.52 * RATE_BTC,
    lastSyncedAt: iso(-1 * HOUR),
  },
];

const ethBalances: VaultBalance[] = [
  {
    id: 'demo-bal-eth-0',
    vaultId: DEMO_VAULT_IDS.eth,
    addressIndex: 0,
    chain: 'eth',
    address: at(ethAddresses, 0).address,
    balance: '4250000000000000000', // 4.25 ETH
    unconfirmedBalance: '0',
    balanceUsd: 4.25 * RATE_ETH,
    tokens: [
      {
        contract: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
        symbol: 'USDC',
        name: 'USD Coin',
        balance: '120000000000', // 120,000 USDC (6 decimals)
        decimals: 6,
        balanceUsd: 120000,
      },
      {
        contract: '0xdAC17F958D2ee523a2206206994597C13D831ec7',
        symbol: 'USDT',
        name: 'Tether USD',
        balance: '45000000000', // 45,000 USDT (6 decimals)
        decimals: 6,
        balanceUsd: 45000,
      },
    ],
    lastSyncedAt: iso(-30 * 60_000),
  },
];

const coldBalances: VaultBalance[] = [
  {
    id: 'demo-bal-cold-0',
    vaultId: DEMO_VAULT_IDS.cold,
    addressIndex: 0,
    chain: 'flux',
    address: at(coldAddresses, 0).address,
    balance: '125000000000', // 1,250 FLUX (8 decimals)
    unconfirmedBalance: '0',
    balanceUsd: 1250 * RATE_FLUX,
    lastSyncedAt: iso(-2 * DAY),
  },
  {
    id: 'demo-bal-cold-1',
    vaultId: DEMO_VAULT_IDS.cold,
    addressIndex: 1,
    chain: 'flux',
    address: at(coldAddresses, 1).address,
    balance: '88000000000', // 880 FLUX
    unconfirmedBalance: '0',
    balanceUsd: 880 * RATE_FLUX,
    lastSyncedAt: iso(-2 * DAY),
  },
];

const balancesByVault: Record<string, VaultBalance[]> = {
  [DEMO_VAULT_IDS.btc]: btcBalances,
  [DEMO_VAULT_IDS.eth]: ethBalances,
  [DEMO_VAULT_IDS.cold]: coldBalances,
};

// Fill vault.totalBalanceUsd from the seeded balances (single source of truth).
const sumUsd = (bals: VaultBalance[]): number =>
  bals.reduce(
    (acc, b) =>
      acc + (b.balanceUsd ?? 0) + (b.tokens ?? []).reduce((t, tok) => t + (tok.balanceUsd ?? 0), 0),
    0
  );
btcVault.totalBalanceUsd = sumUsd(btcBalances);
ethVault.totalBalanceUsd = sumUsd(ethBalances);
coldVault.totalBalanceUsd = sumUsd(coldBalances);

export const demoVaults: Vault[] = [btcVault, ethVault, coldVault];

export const demoVaultDetails: Record<string, VaultDetail> = {
  [DEMO_VAULT_IDS.btc]: { ...btcVault, memberCount: 3, pendingXpubCount: 0 },
  [DEMO_VAULT_IDS.eth]: { ...ethVault, memberCount: 2, pendingXpubCount: 0 },
  [DEMO_VAULT_IDS.cold]: { ...coldVault, memberCount: 5, pendingXpubCount: 0 },
};

// ============================================================
// Vault members (drive signer lists in the proposal + walkthrough)
// ============================================================

const mkVaultMember = (
  vaultId: string,
  wkIdentity: string,
  role: VaultMember['role'],
  displayName: string,
  email: string,
  idx: number
): VaultMember => ({
  id: `demo-vm-${vaultId}-${String(idx)}`,
  vaultId,
  organizationId: DEMO_ORG_ID,
  wkIdentity,
  role,
  xpubStatus: 'submitted',
  addedBy: DEMO_WK.owner,
  addedAt: iso(-150 * DAY),
  xpubSubmittedAt: iso(-149 * DAY),
  displayName,
  email,
});

const membersByVault: Record<string, VaultMember[]> = {
  [DEMO_VAULT_IDS.btc]: [
    mkVaultMember(
      DEMO_VAULT_IDS.btc,
      DEMO_WK.owner,
      'admin',
      'Dana Okafor',
      'treasury@acme.example',
      0
    ),
    mkVaultMember(
      DEMO_VAULT_IDS.btc,
      DEMO_WK.admin,
      'signer',
      'Marco Reyes',
      'cfo@acme.example',
      1
    ),
    mkVaultMember(
      DEMO_VAULT_IDS.btc,
      DEMO_WK.member,
      'signer',
      'Priya Nair',
      'ops@acme.example',
      2
    ),
  ],
  [DEMO_VAULT_IDS.eth]: [
    mkVaultMember(
      DEMO_VAULT_IDS.eth,
      DEMO_WK.owner,
      'admin',
      'Dana Okafor',
      'treasury@acme.example',
      0
    ),
    mkVaultMember(
      DEMO_VAULT_IDS.eth,
      DEMO_WK.admin,
      'signer',
      'Marco Reyes',
      'cfo@acme.example',
      1
    ),
  ],
  [DEMO_VAULT_IDS.cold]: [
    mkVaultMember(
      DEMO_VAULT_IDS.cold,
      DEMO_WK.owner,
      'admin',
      'Dana Okafor',
      'treasury@acme.example',
      0
    ),
    mkVaultMember(
      DEMO_VAULT_IDS.cold,
      DEMO_WK.admin,
      'signer',
      'Marco Reyes',
      'cfo@acme.example',
      1
    ),
    mkVaultMember(
      DEMO_VAULT_IDS.cold,
      DEMO_WK.member,
      'signer',
      'Priya Nair',
      'ops@acme.example',
      2
    ),
  ],
};

// ============================================================
// Transactions (mixed inbound/outbound per vault)
// ============================================================

const btcTransactions: VaultTransaction[] = [
  {
    id: 'demo-tx-btc-1',
    vaultId: DEMO_VAULT_IDS.btc,
    chain: 'btc',
    txid: 'demoBtcTxIncoming0000000000000000000000000000000000000000000000a1',
    direction: 'incoming',
    from: 'bc1qexternaldepositdemoxxxxxxxxxxxxxxxxxxxxx',
    to: at(btcAddresses, 0).address,
    amount: '50000000',
    amountUsd: 0.5 * RATE_BTC,
    rateUsd: demoCryptoRates.btc,
    confirmations: 412,
    blockHeight: 870123,
    timestamp: iso(-30 * DAY),
  },
  {
    id: 'demo-tx-btc-2',
    vaultId: DEMO_VAULT_IDS.btc,
    chain: 'btc',
    txid: 'demoBtcTxOutgoing00000000000000000000000000000000000000000000000b2',
    direction: 'outgoing',
    from: at(btcAddresses, 0).address,
    to: 'bc1qvendorpayoutdemoxxxxxxxxxxxxxxxxxxxxxxxxx',
    amount: '12000000',
    fee: '4200',
    amountUsd: 0.12 * RATE_BTC,
    rateUsd: demoCryptoRates.btc,
    confirmations: 88,
    blockHeight: 870987,
    timestamp: iso(-6 * DAY),
  },
];

const ethTransactions: VaultTransaction[] = [
  {
    id: 'demo-tx-eth-1',
    vaultId: DEMO_VAULT_IDS.eth,
    chain: 'eth',
    txid: '0xdemoEthIncoming000000000000000000000000000000000000000000000000c3',
    direction: 'incoming',
    from: '0xExternalFunderDemo00000000000000000000aB',
    to: at(ethAddresses, 0).address,
    amount: '2000000000000000000',
    amountUsd: 2 * RATE_ETH,
    rateUsd: demoCryptoRates.eth,
    confirmations: 1240,
    timestamp: iso(-14 * DAY),
  },
  {
    id: 'demo-tx-eth-2',
    vaultId: DEMO_VAULT_IDS.eth,
    chain: 'eth',
    txid: '0xdemoUsdcPayroll00000000000000000000000000000000000000000000000d4',
    direction: 'outgoing',
    from: at(ethAddresses, 0).address,
    to: '0xPayrollProviderDemo000000000000000000aB',
    amount: '60000000000', // 60,000 USDC
    amountUsd: 60000,
    tokenContract: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
    tokenSymbol: 'USDC',
    tokenDecimals: 6,
    confirmations: 320,
    timestamp: iso(-3 * DAY),
  },
];

const coldTransactions: VaultTransaction[] = [
  {
    id: 'demo-tx-cold-1',
    vaultId: DEMO_VAULT_IDS.cold,
    chain: 'flux',
    txid: 'demoFluxColdDeposit000000000000000000000000000000000000000000000e5',
    direction: 'incoming',
    from: 't1externalfluxdepositdemoxxxxxxxxxxxxx',
    to: at(coldAddresses, 0).address,
    amount: '50000000000', // 500 FLUX
    amountUsd: 500 * RATE_FLUX,
    rateUsd: demoCryptoRates.flux,
    confirmations: 5400,
    timestamp: iso(-45 * DAY),
  },
];

const transactionsByVault: Record<string, VaultTransaction[]> = {
  [DEMO_VAULT_IDS.btc]: btcTransactions,
  [DEMO_VAULT_IDS.eth]: ethTransactions,
  [DEMO_VAULT_IDS.cold]: coldTransactions,
};

// ============================================================
// 90-day balance history per vault (for charts)
// ============================================================

const buildHistory = (
  vaultId: string,
  chain: string,
  endBalanceUsd: number,
  endBalanceRaw: string,
  rate: number
): VaultBalanceHistory[] => {
  const points: VaultBalanceHistory[] = [];
  const endRawBig = BigInt(endBalanceRaw);
  for (let d = 90; d >= 0; d -= 3) {
    // Gentle deterministic ramp from ~70% to 100% with a small sine wobble.
    const progress = (90 - d) / 90;
    const wobble = 1 + 0.04 * Math.sin(d / 6);
    const factor = (0.7 + 0.3 * progress) * wobble;
    const rawBal = ((endRawBig * BigInt(Math.round(factor * 1000))) / 1000n).toString();
    points.push({
      id: `demo-hist-${vaultId}-${String(d)}`,
      vaultId,
      chain,
      totalBalance: rawBal,
      totalBalanceUsd: Math.round(endBalanceUsd * factor),
      rateUsd: rate,
      addressBalances: [{ addressIndex: 0, balance: rawBal }],
      snapshotAt: iso(-d * DAY),
    });
  }
  return points;
};

const historyByVault: Record<string, VaultBalanceHistory[]> = {
  [DEMO_VAULT_IDS.btc]: buildHistory(
    DEMO_VAULT_IDS.btc,
    'btc',
    sumUsd(btcBalances),
    '236000000',
    RATE_BTC
  ),
  [DEMO_VAULT_IDS.eth]: buildHistory(
    DEMO_VAULT_IDS.eth,
    'eth',
    sumUsd(ethBalances),
    '4250000000000000000',
    RATE_ETH
  ),
  [DEMO_VAULT_IDS.cold]: buildHistory(
    DEMO_VAULT_IDS.cold,
    'flux',
    sumUsd(coldBalances),
    '213000000000',
    RATE_FLUX
  ),
};

// ============================================================
// Policies (velocity limit, whitelist, + one recorded violation)
// ============================================================

const ethPolicy: VaultPolicy = {
  id: 'demo-policy-eth',
  organizationId: DEMO_ORG_ID,
  vaultId: DEMO_VAULT_IDS.eth,
  aggregateLimits: {
    perTransaction: { usd: 100000 },
    daily: { usd: 150000 },
    weekly: { usd: 500000 },
    monthly: { usd: 1500000 },
  },
  tokenLimits: [
    {
      symbol: 'USDC',
      contractAddress: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
      decimals: 6,
      dailyUsd: 100000,
    },
  ],
  whitelist: {
    mode: 'warn',
    addresses: [
      {
        address: '0xPayrollProviderDemo000000000000000000aB',
        label: 'Payroll Provider',
        chain: 'eth',
        addedBy: DEMO_WK.owner,
        addedAt: iso(-100 * DAY),
      },
    ],
  },
  timeLock: {
    enabled: true,
    delayMinutes: 60,
    thresholdUsd: 100000,
  },
  approvalOverrides: {
    highValueThresholdUsd: 250000,
    requireAdminApproval: true,
  },
  updatedBy: DEMO_WK.owner,
  updatedAt: iso(-5 * DAY),
  createdAt: iso(-150 * DAY),
};

const policyByVault: Record<string, VaultPolicy> = {
  [DEMO_VAULT_IDS.eth]: ethPolicy,
};

// ============================================================
// Proposals (≥2 pending; the EVM payroll hero pre-staged at 1-of-2)
// ============================================================

/**
 * HERO: EVM USDC payroll proposal pre-staged at "1 of 2 signatures collected"
 * so the signature walkthrough can play the final co-sign. Carries a
 * policyCheck (whitelist warn) so the policy surface is non-empty.
 */
const heroPayrollProposal: TxProposal = {
  id: DEMO_HERO_PROPOSAL_ID,
  vaultId: DEMO_VAULT_IDS.eth,
  organizationId: DEMO_ORG_ID,
  chain: 'eth',
  proposedBy: DEMO_WK.admin,
  recipients: [{ address: '0xPayrollProviderDemo000000000000000000aB', amount: '48000000000' }], // 48,000 USDC
  fee: '320000000000000', // ~0.00032 ETH
  memo: 'June payroll run',
  orgIndex: DEMO_ORG_INDEX,
  vaultIndex: 1,
  rawUnsignedTx: 'demo-unsigned-eth-userop',
  inputDetails: [{ index: 0, addressIndex: 0, amount: '48000000000' }],
  requiredSignatures: 2,
  signatureCount: 1,
  rejectionCount: 0,
  signatures: [{ wkIdentity: DEMO_WK.admin, signedAt: iso(-30 * 60_000) }],
  rejections: [],
  status: 'pending',
  signingMode: 'dual',
  designatedSigners: [DEMO_WK.admin, DEMO_WK.owner],
  tokenContract: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
  tokenSymbol: 'USDC',
  tokenDecimals: 6,
  policyCheck: {
    status: 'requires_approval',
    violations: [
      {
        type: 'whitelist',
        message: 'Recipient is on the whitelist (warn mode) — review before approving.',
      },
    ],
    evaluatedAt: iso(-31 * 60_000),
    amountUsd: 48000,
  },
  createdAt: iso(-35 * 60_000),
  expiresAt: iso(20 * HOUR),
  updatedAt: iso(-30 * 60_000),
};

/** A UTXO BTC vendor payout, also pending (0-of-2). */
const btcVendorProposal: TxProposal = {
  id: 'demo-proposal-btc-vendor',
  vaultId: DEMO_VAULT_IDS.btc,
  organizationId: DEMO_ORG_ID,
  chain: 'btc',
  proposedBy: DEMO_WK.owner,
  recipients: [{ address: 'bc1qvendorpayoutdemoxxxxxxxxxxxxxxxxxxxxxxxxx', amount: '8000000' }], // 0.08 BTC
  fee: '3800',
  memo: 'Q2 vendor settlement',
  orgIndex: DEMO_ORG_INDEX,
  vaultIndex: 0,
  rawUnsignedTx: 'demo-unsigned-btc-psbt',
  inputDetails: [
    { index: 0, addressIndex: 0, witnessScript: 'DEMO_WITNESS_SCRIPT_0', amount: '8003800' },
  ],
  requiredSignatures: 2,
  signatureCount: 0,
  rejectionCount: 0,
  signatures: [],
  rejections: [],
  status: 'pending',
  signingMode: 'dual',
  createdAt: iso(-3 * HOUR),
  expiresAt: iso(45 * HOUR),
  updatedAt: iso(-3 * HOUR),
};

/** A confirmed historical proposal so the proposals list isn't all-pending. */
const ethConfirmedProposal: TxProposal = {
  id: 'demo-proposal-eth-confirmed',
  vaultId: DEMO_VAULT_IDS.eth,
  organizationId: DEMO_ORG_ID,
  chain: 'eth',
  proposedBy: DEMO_WK.admin,
  recipients: [{ address: '0xVendorDemo000000000000000000000000000aB', amount: '12000000000' }],
  fee: '300000000000000',
  memo: 'SaaS renewal',
  orgIndex: DEMO_ORG_INDEX,
  vaultIndex: 1,
  requiredSignatures: 2,
  signatureCount: 2,
  rejectionCount: 0,
  signatures: [
    { wkIdentity: DEMO_WK.admin, signedAt: iso(-3 * DAY) },
    { wkIdentity: DEMO_WK.owner, signedAt: iso(-3 * DAY + 60_000) },
  ],
  rejections: [],
  status: 'confirmed',
  signingMode: 'dual',
  tokenContract: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
  tokenSymbol: 'USDC',
  tokenDecimals: 6,
  broadcastTxid: at(ethTransactions, 1).txid,
  broadcastAt: iso(-3 * DAY + 120_000),
  confirmedAt: iso(-3 * DAY + 300_000),
  createdAt: iso(-3 * DAY - HOUR),
  expiresAt: iso(-2 * DAY),
  updatedAt: iso(-3 * DAY + 300_000),
};

const proposalsByVault: Record<string, TxProposal[]> = {
  [DEMO_VAULT_IDS.btc]: [btcVendorProposal],
  [DEMO_VAULT_IDS.eth]: [heroPayrollProposal, ethConfirmedProposal],
  [DEMO_VAULT_IDS.cold]: [],
};

export const demoProposals: TxProposal[] = [
  heroPayrollProposal,
  btcVendorProposal,
  ethConfirmedProposal,
];

// Signing requests surfaced to the demo user (proposals awaiting their sig).
export const demoSigningRequests: Array<{
  proposal: TxProposal;
  vaultId: string;
  vaultName: string;
}> = [
  { proposal: heroPayrollProposal, vaultId: DEMO_VAULT_IDS.eth, vaultName: ethVault.name },
  { proposal: btcVendorProposal, vaultId: DEMO_VAULT_IDS.btc, vaultName: btcVault.name },
];

// ============================================================
// Audit + critical-action logs
// ============================================================

export const demoOrgAuditLogs: OrgAuditLog[] = [
  {
    id: 'demo-audit-1',
    organizationId: DEMO_ORG_ID,
    eventType: 'org_created',
    actorWkIdentity: DEMO_WK.owner,
    details: { name: 'Acme Capital' },
    createdAt: iso(-180 * DAY),
  },
  {
    id: 'demo-audit-2',
    organizationId: DEMO_ORG_ID,
    eventType: 'member_joined',
    actorWkIdentity: DEMO_WK.admin,
    targetWkIdentity: DEMO_WK.admin,
    details: { role: 'admin' },
    createdAt: iso(-160 * DAY),
  },
  {
    id: 'demo-audit-3',
    organizationId: DEMO_ORG_ID,
    eventType: 'member_role_changed',
    actorWkIdentity: DEMO_WK.owner,
    targetWkIdentity: DEMO_WK.member,
    details: { from: 'viewer', to: 'member' },
    createdAt: iso(-90 * DAY),
  },
];

export const demoCriticalActionLogs: CriticalActionLogEntry[] = [
  {
    id: 'demo-critical-1',
    timestamp: iso(-90 * DAY),
    action: 'promote_to_admin',
    wkIdentity: DEMO_WK.owner,
    targetOrgId: DEMO_ORG_ID,
    targetWkIdentity: DEMO_WK.admin,
    phase: 'action_completed',
    success: true,
  },
  {
    id: 'demo-critical-2',
    timestamp: iso(-20 * DAY),
    action: 'freeze_vault',
    wkIdentity: DEMO_WK.owner,
    targetOrgId: DEMO_ORG_ID,
    phase: 'action_completed',
    success: true,
  },
];

// ============================================================
// Per-vault lookup helpers (used by seed + fixture adapter)
// ============================================================

export const demoVaultData = {
  details: demoVaultDetails,
  addresses: addressesByVault,
  balances: balancesByVault,
  transactions: transactionsByVault,
  history: historyByVault,
  members: membersByVault,
  proposals: proposalsByVault,
  policy: policyByVault,
};
